// صفحه‌ی «برنامه‌های من» — منطقِ خالص، بدونِ react-native و بدونِ DOM
// (WHOSUP-135). هر دو اپ همین دسته‌بندی و همین شبکه‌ی تقویم را می‌کشند؛ دو
// نسخه یعنی دو تعریف از «برنامه‌ی من چیست».
//
// ⚠ تصمیمِ ثبت‌شده: سیگنالِ قطعی‌شده **هیچ‌وقت** در public.events نوشته
// نمی‌شود — آن جدول کاتالوگِ عمومیِ Discover است و «شام خانه‌ی نگار» آن‌جا
// یعنی شکستنِ قاعده‌ی ضد-FOMO. این صفحه فقط از signals/plans می‌خواند.
import type { Signal } from '../domain/types.ts';

/**
 * رابطه‌ی من با هر برنامه — همان شش حالتی که رنگ‌هایش تصمیمِ صریحِ کاربر است:
 *   confirmedMine   سبزِ پررنگ (accent) — قطعی، ساخته‌ی خودم
 *   confirmedOther  سبزِ روشن (accent2) — قطعی، ساخته‌ی دیگری
 *   inPending       سبزِ توخالی — گفته‌ام «هستم» (یا مالِ خودم است) ولی قطعی نشده
 *   maybeish        زردِ کم‌رنگ (warn) — «شاید» یا «شرطی»
 *   unanswered      نارنجی (notice) — هنوز جواب نداده‌ام
 *   null            نمایش داده نمی‌شود — «نیستم»، لغوشده، یا منقضیِ بی‌جواب
 */
export type PlanBucket =
  | 'confirmedMine' | 'confirmedOther' | 'inPending' | 'maybeish' | 'unanswered';

export interface PlanEntry {
  signal: Signal;
  bucket: PlanBucket;
  /** لحظه‌ی برنامه: زمانِ نهاییِ قطعی‌شده، وگرنه زمانِ خودِ سیگنال. */
  when: string | null;
}

export function planBucket(s: Signal, myId: string): PlanBucket | null {
  if (s.status === 'cancelled') return null;
  const mine = s.creatorId === myId;
  if (!mine && s.myResponse === 'out') return null;
  if (s.status === 'confirmed' || s.status === 'completed') {
    return mine ? 'confirmedMine' : 'confirmedOther';
  }
  if (mine || s.myResponse === 'in') return 'inPending';
  if (s.myResponse === 'maybe' || s.myResponse === 'conditional') return 'maybeish';
  // دعوتِ منقضی که هیچ‌وقت جوابش ندادم نه برنامه است نه کاری مانده.
  if (s.status === 'expired') return null;
  return 'unanswered';
}

/**
 * برچسبِ ردیفِ فهرست (WHOSUP-342): اول جوابِ خودم، بعد «/» و وضعیتِ برنامه —
 * «هستم / قطعی شد». قطعی بودن را سازنده تعیین می‌کند و هستم/شاید را دعوت‌شده،
 * پس دو چیزِ جدا هستند و یک برچسبِ ترکیبی («قطعی — دعوت شده‌ام») جوابِ من را
 * پنهان می‌کرد. خروجی کلیدهای i18n است.
 */
export function planRowLabelKeys(s: Signal, myId: string): [string, string] {
  const answer = s.creatorId === myId ? 'planMineShort'
    : s.myResponse === 'in' ? 'shortIn'
    : s.myResponse === 'maybe' ? 'shortMaybe'
    : s.myResponse === 'conditional' ? 'conditionalShort'
    : 'planUnanswered';
  const state = s.status === 'confirmed' || s.status === 'completed'
    ? 'statusConfirmed' : 'planNotFinal';
  return [answer, state];
}

export const planWhen =(s: Signal): string | null =>
  s.plan?.finalStartsAt ?? s.startsAt ?? null;

/** دسته‌بندی + مرتب‌سازی صعودی بر زمان؛ بی‌تاریخ‌ها ته فهرست. */
export function planEntries(signals: Signal[], myId: string): PlanEntry[] {
  const seen = new Set<string>();
  const out: PlanEntry[] = [];
  for (const s of signals) {
    if (seen.has(s.id)) continue;
    seen.add(s.id);
    const bucket = planBucket(s, myId);
    if (!bucket) continue;
    out.push({ signal: s, bucket, when: planWhen(s) });
  }
  return out.sort((a, b) => {
    if (!a.when) return b.when ? 1 : 0;
    if (!b.when) return -1;
    return a.when.localeCompare(b.when);
  });
}

/**
 * گذشته/آینده — با همان ۶ ساعت اغماضِ چرخه‌ی عمر (مایگریشن 0127): برنامه‌ی
 * امشب ساعت ۸، ساعتِ ۹ هنوز «گذشته» نیست.
 */
export function splitEntries(entries: PlanEntry[], now: Date): {
  upcoming: PlanEntry[]; past: PlanEntry[]; undated: PlanEntry[];
} {
  const edge = now.getTime() - 6 * 3600_000;
  const upcoming: PlanEntry[] = [];
  const past: PlanEntry[] = [];
  const undated: PlanEntry[] = [];
  for (const e of entries) {
    if (!e.when) undated.push(e);
    else if (new Date(e.when).getTime() < edge) past.push(e);
    else upcoming.push(e);
  }
  // گذشته‌ها نزدیک‌ترین اول — همان چیزی که آدم دنبالش می‌گردد.
  past.reverse();
  return { upcoming, past, undated };
}

const two = (n: number): string => String(n).padStart(2, '0');

/** کلیدِ روزِ **محلی** یک لحظه — تقویم را با روزِ دستگاهِ کاربر می‌چینیم. */
export const dayKey = (iso: string): string => {
  const d = new Date(iso);
  return `${d.getFullYear()}-${two(d.getMonth() + 1)}-${two(d.getDate())}`;
};

/** شنبه برای زبان‌های راست‌به‌چپِ منطقه، وگرنه یکشنبه — قراردادِ تقویمِ محلی. */
export const weekStartFor = (locale: string): number =>
  (locale === 'fa' || locale === 'ar' || locale === 'ur' ? 6 : 0);

/**
 * شبکه‌ی یک ماه: هفته‌های کامل، هر خانه یا کلیدِ روزِ همین ماه است یا null
 * (خانه‌ی خالیِ اول/آخر). `month0` صفرمبناست، مثل خودِ Date.
 */
export function monthGrid(year: number, month0: number, weekStart: number): (string | null)[][] {
  const first = new Date(year, month0, 1);
  const daysInMonth = new Date(year, month0 + 1, 0).getDate();
  const lead = (first.getDay() - weekStart + 7) % 7;
  const cells: (string | null)[] = Array.from({ length: lead }, () => null);
  for (let d = 1; d <= daysInMonth; d += 1) {
    cells.push(`${year}-${two(month0 + 1)}-${two(d)}`);
  }
  while (cells.length % 7 !== 0) cells.push(null);
  const weeks: (string | null)[][] = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  return weeks;
}

export interface MonthCell { day: string; inMonth: boolean }

/**
 * مثلِ monthGrid ولی خانه‌های اول/آخر خالی نیستند — روزهای واقعیِ ماهِ قبل و
 * بعدند، با پرچمِ `inMonth: false` تا کم‌رنگ کشیده شوند (WHOSUP-157، به
 * خواستِ کاربر). تابعِ جدا و نه تغییرِ monthGrid: آن یکی رفتارِ خودش را
 * دارد (خانه‌ی خالی به‌جای روزِ ماهِ دیگر) و تستِ خودش را.
 */
export function monthGridPadded(year: number, month0: number, weekStart: number): MonthCell[][] {
  const first = new Date(year, month0, 1);
  const daysInMonth = new Date(year, month0 + 1, 0).getDate();
  const lead = (first.getDay() - weekStart + 7) % 7;
  const total = Math.ceil((lead + daysInMonth) / 7) * 7;
  const weeks: MonthCell[][] = [];
  for (let i = 0; i < total; i += 7) {
    const row: MonthCell[] = [];
    for (let j = 0; j < 7; j += 1) {
      const offset = i + j - lead;
      // روز به خودِ Date داده می‌شود تا مرزِ ماه/سال خودش درست بیفتد.
      const d = new Date(year, month0, 1 + offset);
      row.push({
        day: `${d.getFullYear()}-${two(d.getMonth() + 1)}-${two(d.getDate())}`,
        inMonth: offset >= 0 && offset < daysInMonth,
      });
    }
    weeks.push(row);
  }
  return weeks;
}

/**
 * دو هفته‌ی جاری: هفته‌ای که «امروز» تویش است + هفته‌ی بعد (WHOSUP-157).
 * برای صفحه‌ی باریک، که شبکه‌ی کاملِ ماه جا ندارد. برخلافِ monthGrid خانه‌ی
 * خالی ندارد — هر ۱۴ خانه روزِ واقعی‌اند، حتی اگر از دو ماه بیایند.
 */
export function twoWeekGrid(today: Date, weekStart: number): string[][] {
  const lead = (today.getDay() - weekStart + 7) % 7;
  const weeks: string[][] = [];
  for (let w = 0; w < 2; w += 1) {
    const row: string[] = [];
    for (let d = 0; d < 7; d += 1) {
      // روز به روزِ خودِ Date اضافه می‌شود تا مرزِ ماه/سال خودش درست بیفتد.
      const day = new Date(
        today.getFullYear(), today.getMonth(), today.getDate() - lead + w * 7 + d,
      );
      row.push(`${day.getFullYear()}-${two(day.getMonth() + 1)}-${two(day.getDate())}`);
    }
    weeks.push(row);
  }
  return weeks;
}

/**
 * آیا در این شبکه اصلاً برنامه‌ای هست؟ (WHOSUP-235)
 *
 * تقویمِ دو هفته‌ای که هیچ نشانی ندارد چیزی نمی‌گوید جز «خالی است» — همان
 * حرفی که فهرستِ زیرش خودش می‌زند — و بالای صفحه را به یک شبکه‌ی خاموش
 * می‌دهد. پس وقتی خالی است اصلاً کشیده نمی‌شود. شبکه‌ی کاملِ ماه (وبِ پهن)
 * این قاعده را ندارد: آن‌جا ورق زدن هست و تقویم خودش راهِ گشتنه.
 *
 * `byDay` هر نگاشتِ روز→فهرست را می‌پذیرد؛ موبایل دسته‌ها را نگه می‌دارد و
 * وب خودِ ردیف‌ها را، و برای این سوال فرقی ندارد.
 */
export const hasPlansIn = (
  weeks: string[][], byDay: ReadonlyMap<string, readonly unknown[]>,
): boolean => weeks.some((w) => w.some((d) => (byDay.get(d)?.length ?? 0) > 0));

/*
 * «نمای انتخابی» (getPlansView/setPlansView) این‌جا بود و با WHOSUP-165 رفت:
 * تنها مصرف‌کننده‌اش سوییچِ «لیست / تقویم» موبایل بود، و آن سوییچ خودش حذف
 * شد — حالا هر دو اپ تقویم و لیست را با هم نشان می‌دهند، پس چیزی برای
 * انتخاب و به‌یاد سپردن نمانده.
 */
